import { Component, OnInit } from '@angular/core';
import { CDATAConnectorInfo } from '../../../../../../../models/datamodel/adbinfo.model';
import { DataModelService } from '../../../../../../../services/datamodel.service';
import { OAuthService } from '../../../../../../../services/oauth.service';
import { ACDATAConnectorPanel } from '../a.cdata.connector.panel';

/** A connection property as CDataTreiberRest describes it (from the driver's own metadata). */
interface CDataProperty {
    Name: string;
    Caption: string;
    Description: string;
    Category: string;
    Type: 'string' | 'bool' | 'int';
    Default: string;
    Values: string[];
    Secret: boolean;
}

interface PropertyGroup {
    Category: string;
    Properties: CDataProperty[];
    Expanded: boolean;
}

/**
 * Any CData driver of the build (GenericDriverInfo). The form is built from the driver's connection
 * properties; only values that differ from the driver's default are stored.
 */
@Component({
    selector: 'genericdriver-connector-panel',
    templateUrl: './genericdriver.connector.panel.html'
})
export class GenericDriverConnectorPanel extends ACDATAConnectorPanel implements OnInit {
    static GetInfo(): CDATAConnectorInfo {
        return {
            Caption: '@@Other CData source',
            Type: 'evidanza.Database.CData.CDataConnector.ConnectorsInfo.GenericDriverInfo',
            ControlType: GenericDriverConnectorPanel,
            // No Properties object here: Object.assign would hand the same one to every new data source.
            Default: {
                Driver: null,
                WebOAuth: false
            }
        };
    }

    // Shown first and open; the rest follows alphabetically, closed.
    private static readonly MainCategories = ['Authentication', '', 'Connection', 'OAuth'];
    // With sign-in through GA these are set by GA (GenericDriverInfo.Bauen).
    private static readonly SetByGA = ['OAuthAccessToken', 'CallbackURL', 'InitiateOAuth'];

    Drivers: { Name: string, Caption: string }[] = [];
    DriverText = '';
    Properties: CDataProperty[] = [];
    Groups: PropertyGroup[] = [];
    PropertyFilter = '';
    Loading = false;
    LoadError: string = null;

    constructor(private dataService: DataModelService, private oauthService: OAuthService) {
        super();
    }

    ngOnInit(): void {
        if (this.Settings && !this.Settings['Properties']) {
            this.Settings['Properties'] = {};
        }
        this.dataService.GetCDataDrivers().subscribe(list => {
            this.Drivers = list || [];
            this.DriverText = this.captionOf(this.Settings?.['Driver']);
        });
        if (this.Settings?.['Driver']) {
            this.loadProperties();
        }
    }

    get FilteredDrivers() {
        const text = (this.DriverText || '').toLowerCase();
        return text ? this.Drivers.filter(x => x.Caption.toLowerCase().includes(text)) : this.Drivers;
    }

    get HasOAuth(): boolean {
        return this.Properties.some(x => x.Name === 'OAuthClientId');
    }

    captionOf(driver: string): string {
        return this.Drivers.find(x => x.Name === driver)?.Caption ?? (driver ? driver.substring(driver.lastIndexOf('.') + 1) : '');
    }

    onDriverSelected(driver: string) {
        if (driver === this.Settings['Driver']) {
            this.DriverText = this.captionOf(driver);
            return;
        }
        this.Settings['Driver'] = driver;
        this.Settings['Properties'] = {};
        this.Settings['WebOAuth'] = false;
        this.DriverText = this.captionOf(driver);
        this.loadProperties();
        this.onChange();
    }

    loadProperties() {
        this.Properties = [];
        this.Groups = [];
        this.LoadError = null;
        this.Loading = true;
        this.dataService.GetCDataProperties(this.Settings['Driver']).subscribe({
            next: list => {
                this.Loading = false;
                this.Properties = list || [];
                this.buildGroups();
            },
            error: err => {
                this.Loading = false;
                this.LoadError = err?.error?.toString?.() || err?.message || '';
            }
        });
    }

    buildGroups() {
        const filter = (this.PropertyFilter || '').toLowerCase();
        const hidden = this.Settings['WebOAuth'] ? GenericDriverConnectorPanel.SetByGA : [];
        const visible = this.Properties.filter(p => !hidden.includes(p.Name)
            && (!filter || p.Caption.toLowerCase().includes(filter) || p.Name.toLowerCase().includes(filter)));
        const categories = [...new Set(visible.map(p => p.Category || ''))].sort((a, b) => {
            const ia = GenericDriverConnectorPanel.MainCategories.indexOf(a);
            const ib = GenericDriverConnectorPanel.MainCategories.indexOf(b);
            if (ia >= 0 || ib >= 0) {
                return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
            }
            return a.localeCompare(b);
        });
        const wasOpen = new Map(this.Groups.map(g => [g.Category, g.Expanded]));
        this.Groups = categories.map(c => ({
            Category: c,
            Properties: visible.filter(p => (p.Category || '') === c),
            // While searching every hit is visible; otherwise the main groups and those the user opened.
            Expanded: !!filter || (wasOpen.get(c) ?? (GenericDriverConnectorPanel.MainCategories.indexOf(c) >= 0 && GenericDriverConnectorPanel.MainCategories.indexOf(c) < 2))
        }));
    }

    getValue(p: CDataProperty): string {
        return this.Settings['Properties']?.[p.Name] ?? '';
    }

    isChecked(p: CDataProperty): boolean {
        return (this.getValue(p) || p.Default) === 'true';
    }

    setValue(p: CDataProperty, value: any) {
        const text = value === null || value === undefined ? '' : String(value);
        if (text === '' || text === p.Default) {
            delete this.Settings['Properties'][p.Name];
        } else {
            this.Settings['Properties'][p.Name] = text;
        }
        this.onChange();
    }

    onWebOAuthChanged() {
        this.buildGroups();
        this.onChange();
    }

    authenticate(): void {
        if (this.DataSource && this.DataSource.SID) {
            this.oauthService.GetOAuthURL(this.DataSource.SID, 'CData').subscribe(x => {
                if (x) {
                    window.open(x, '_blank');
                }
            });
        }
    }
}
